import { Card, CardContent } from "@/components/ui/card";
import { Profile } from "@/types/database.types";
import { UserHeader } from "./UserHeader";
import { UserTypeSelector } from "./UserTypeSelector";
import { AdminToggle } from "./AdminToggle";
import { isSuperAdmin } from "@/services/admin.service";
import { useState, useEffect } from "react";

interface UserCardProps {
  user: Profile;
  isAdmin: boolean;
  formatDate: (date: string) => string;
  getInitials: (firstName?: string | null, lastName?: string | null) => string;
  onAdminStatusChange: () => void;
}

export const UserCard = ({ 
  user, 
  isAdmin,
  formatDate, 
  getInitials,
  onAdminStatusChange
}: UserCardProps) => {
  const [isUserSuperAdmin, setIsUserSuperAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user?.id) {
        try {
          const isAdmin = await isSuperAdmin(user.id);
          setIsUserSuperAdmin(isAdmin);
        } catch (error) {
          console.error('Error checking admin status:', error);
          setIsUserSuperAdmin(false);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    checkAdminStatus();
  }, [user]);

  return (
    <Card className="glass-card overflow-hidden border-primary/10 transition-all duration-300 hover:shadow-md">
      <UserHeader 
        user={user}
        isAdmin={isAdmin}
        getInitials={getInitials}
      />
      
      <CardContent className="p-4">
        <div className="mb-2 flex justify-between">
          <span className="text-sm text-muted-foreground">Joined</span>
          <span className="text-sm">{formatDate(user.created_at || '')}</span>
        </div>
        
        <div className="mb-2">
          <UserTypeSelector 
            user={user}
            isProtectedEmail={isUserSuperAdmin}
            onUserTypeChange={onAdminStatusChange}
          />
        </div>
        
        <div className="mt-4">
          <AdminToggle 
            user={user}
            isAdmin={isAdmin}
            isProtectedEmail={isUserSuperAdmin}
            onAdminStatusChange={onAdminStatusChange}
          />
        </div>

        {/* Display user type */}
        <div className="mt-4 pt-3 border-t border-muted">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">User Type</span>
            <span className="text-sm font-medium capitalize">
              {isUserSuperAdmin ? 'Super Admin' : (user.user_type || 'Regular User')}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
